import { AxiosError } from "axios";
import { all, call, put, takeLatest } from "redux-saga/effects";
import {
  addEditGoalsAssessmentFailure,
  addEditGoalsAssessmentSuccess,
  addPersonalAndSmartGoalsFailure,
  addPersonalAndSmartGoalsSuccess,
  deletePersonalAndSmartGoalsSuccess,
  getCurrentSLPGoalsPDFFailure,
  getCurrentSLPGoalsPDFSuccess,
  hopesGoalsAndAmbitionsFailure,
  hopesGoalsAndAmbitionsSuccess,
  personalAndSmartGoalsBySlpIdSuccess,
  personalAndSmartGoalsFailure,
  personalAndSmartGoalsSuccess,
  updateHopesGoalsAndAmbitionsFailure,
  updateHopesGoalsAndAmbitionsSuccess,
  whereToFromHereFailure,
  whereToFromHereSuccess,
} from "./action";
import {
  ADD_EDIT_GOALS_ASSESSMENT_REQUEST,
  ADD_PERSONAL_SMART_GOALS_REQUEST,
  ADD_WHERE_TO_FROM_HERE_REQUEST,
  DELETE_PERSONAL_SMART_GOALS_REQUEST,
  GET_CURRENT_SLP_GOALS_PDF_REQUEST,
  GET_PERSONAL_SMART_GOALS_BY_SLP_ID_REQUEST,
  GET_PERSONAL_SMART_GOALS_REQUEST,
  HOPES_GOALS_AMBITIONS_REQUEST,
  UPDATE_HOPES_GOALS_AMBITIONS_REQUEST,
} from "./actionTypes";
import slpFormService from "./service";
import {
  AddEditGoalsAssessmentRequest,
  AddPersonalAndSmartGoalsRequest,
  AddPersonalAndSmartGoalsResponse,
  DeletePersonalAndSmartGoalsRequest,
  DeletePersonalAndSmartGoalsResponse,
  GetCurrentSLPGoalsPDFRequest,
  GetCurrentSLPGoalsPDFResponse,
  HopesGoalsAndAmbitionsRequest,
  HopesGoalsAndAmbitionsResponse,
  PersonalAndSmartGoalsBySlpIdRequest,
  PersonalAndSmartGoalsBySlpIdResponse,
  PersonalAndSmartGoalsRequest,
  PersonalAndSmartGoalsResponse,
  UpdateHopesGoalsAndAmbitionsRequest,
  UpdateHopesGoalsAndAmbitionsResponse,
  WhereToFromHereRequest,
  WhereToFromHereResponse,
} from "./types";

function* hopesGoalsAndAmbitionsDataSaga(
  action: HopesGoalsAndAmbitionsRequest
) {
  try {
    const response: HopesGoalsAndAmbitionsResponse = yield call(
      slpFormService.geHopesGoalsAndAmbitions,
      {
        slpId: action.payload.values.slpId,
        partNumber: action.payload.values.partNumber,
      }
    );

    yield put(
      hopesGoalsAndAmbitionsSuccess({
        hopesGoalsAndAmbitionsData: response.item,
      })
    );
    action.payload.callback({
      hopesGoalsAndAmbitionsData: response.item,
    });
  } catch (e) {
    yield put(
      hopesGoalsAndAmbitionsFailure({
        error: (e as AxiosError).message,
      })
    );
  }
}

function* updateHopesGoalsAndAmbitionsSaga(
  action: UpdateHopesGoalsAndAmbitionsRequest
) {
  try {
    const response: UpdateHopesGoalsAndAmbitionsResponse = yield call(
      slpFormService.updateHopesGoalsAndAmbitions,
      {
        slpId: action.payload.values.slpId,
        partNumber: action.payload.values.partNumber,
        allocatedTo: action.payload.values.allocatedTo,
        questionAnswers: action.payload.values.questionAnswers,
      }
    );

    yield put(
      updateHopesGoalsAndAmbitionsSuccess({
        success: response.isSuccessStatusCode,
        message: response.message,
      })
    );
    action.payload.callback({
      success: response.isSuccessStatusCode,
      message: response.message,
    });
  } catch (e) {
    yield put(
      updateHopesGoalsAndAmbitionsFailure({
        error: (e as AxiosError).message,
      })
    );
  }
}

function* addPersonalAndSmartGoalsSaga(
  action: AddPersonalAndSmartGoalsRequest
) {
  try {
    const response: AddPersonalAndSmartGoalsResponse = yield call(
      slpFormService.addPersonalAndSmartGoals,
      {
        slpId: action.payload.values.slpId,
        studentGoals: action.payload.values.studentGoals,
        smartGoals: action.payload.values.smartGoals,
        baselineLevel: action.payload.values.baselineLevel,
        importance: action.payload.values.importance,
        difficulty: action.payload.values.difficulty,
      }
    );

    yield put(
      addPersonalAndSmartGoalsSuccess({
        success: response.isSuccessStatusCode,
        message: response.message,
      })
    );
    action.payload.callback({
      success: response.isSuccessStatusCode,
      message: response.message,
    });
  } catch (e) {
    yield put(
      addPersonalAndSmartGoalsFailure({
        error: (e as AxiosError).message,
      })
    );
  }
}

function* addEditGoalsAssessmentSaga(action: AddEditGoalsAssessmentRequest) {
  try {
    const response: AddPersonalAndSmartGoalsResponse = yield call(
      slpFormService.addEditGoalsAssessment,
      {
        slpGoalsId: action.payload.values.slpGoalsId,
        goalAssessedDate: action.payload.values.goalAssessedDate,
        achievementRatingGAS: action.payload.values.achievementRatingGAS,
        variance: action.payload.values.variance,
      }
    );

    yield put(
      addEditGoalsAssessmentSuccess({
        success: response.isSuccessStatusCode,
        message: response.message,
      })
    );
    action.payload.callback({
      success: response.isSuccessStatusCode,
      message: response.message,
    });
  } catch (e) {
    yield put(
      addEditGoalsAssessmentFailure({
        error: (e as AxiosError).message,
      })
    );
  }
}

function* deletePersonalAndSmartGoalsSaga(
  action: DeletePersonalAndSmartGoalsRequest
) {
  try {
    const response: DeletePersonalAndSmartGoalsResponse = yield call(
      slpFormService.deletePersonalAndSmartGoals,
      {
        id: action.payload.values.id,
      }
    );

    yield put(
      deletePersonalAndSmartGoalsSuccess({
        success: response.isSuccessStatusCode,
      })
    );
    action.payload.callback({
      success: response.isSuccessStatusCode,
    });
  } catch (e) {
    yield put(
      addEditGoalsAssessmentFailure({
        error: (e as AxiosError).message,
      })
    );
  }
}

function* getAllPersonalAndSmartGoalsSaga(
  action: PersonalAndSmartGoalsRequest
) {
  try {
    const response: PersonalAndSmartGoalsResponse = yield call(
      slpFormService.getAllPersonalAndSmartGoals,
      action.payload.values
    );

    const paginationDetails = {
      nextPage: response.item.nextPage,
      prevPage: response.item.prevPage,
      totalCount: response.item.totalRecordCount,
      totalPages: response.item.pageCount,
    };

    yield put(
      personalAndSmartGoalsSuccess({
        personalAndSmartGoalsData: response.item.records,
        personalAndSmartGoalsPagination: paginationDetails,
      })
    );
    action.payload.callback({
      personalAndSmartGoalsData: response.item.records,
      personalAndSmartGoalsPagination: paginationDetails,
    });
  } catch (error) {
    yield put(
      personalAndSmartGoalsFailure({
        error: (error as AxiosError).message,
      })
    );
  }
}

function* getPersonalAndSmartGoalsBySlpIdSaga(
  action: PersonalAndSmartGoalsBySlpIdRequest
) {
  try {
    const response: PersonalAndSmartGoalsBySlpIdResponse = yield call(
      slpFormService.getPersonalAndSmartGoalsBySlpId,
      action.payload.values
    );

    const paginationDetails = {
      nextPage: response.item.nextPage,
      prevPage: response.item.prevPage,
      totalCount: response.item.totalRecordCount,
      totalPages: response.item.pageCount,
    };

    yield put(
      personalAndSmartGoalsBySlpIdSuccess({
        personalAndSmartGoalsData: response.item.records,
        personalAndSmartGoalsPagination: paginationDetails,
      })
    );
    action.payload.callback({
      personalAndSmartGoalsData: response.item.records,
      personalAndSmartGoalsPagination: paginationDetails,
    });
  } catch (error) {
    yield put(
      personalAndSmartGoalsFailure({
        error: (error as AxiosError).message,
      })
    );
  }
}

function* getCurrentSLPGoalsPDFSaga(action: GetCurrentSLPGoalsPDFRequest) {
  try {
    const response: GetCurrentSLPGoalsPDFResponse = yield call(
      slpFormService.getCurrentSLPGoalsPDF,
      action.payload.value
    );
    yield put(
      getCurrentSLPGoalsPDFSuccess({
        goalsPDF: response.item,
      })
    );
    action.payload.callback({
      goalsPDF: response.item,
    });
  } catch (error) {
    yield put(
      getCurrentSLPGoalsPDFFailure({
        error: (error as AxiosError).message,
      })
    );
  }
}

function* addWhereToFromHereSaga(action: WhereToFromHereRequest) {
  try {
    const response: WhereToFromHereResponse = yield call(
      slpFormService.addWhereToFromHere,
      {
        slpId: action.payload.values.slpId,
        partNumber: action.payload.values.partNumber,
        questionAnswers: action.payload.values.questionAnswers,
        studentId: action.payload.values.studentId,
        dateCompleted: action.payload.values.dateCompleted,
        nextSLPDueDate: action.payload.values.nextSLPDueDate,
        preferredCourseDelivery: action.payload.values.preferredCourseDelivery,
      }
    );

    yield put(
      whereToFromHereSuccess({
        success: response.isSuccessStatusCode,
        message: response.message,
      })
    );
    action.payload.callback({
      success: response.isSuccessStatusCode,
      message: response.message,
    });
  } catch (e) {
    yield put(
      whereToFromHereFailure({
        error: (e as AxiosError).message,
      })
    );
  }
}

function* slpFormSaga() {
  yield all([
    takeLatest(HOPES_GOALS_AMBITIONS_REQUEST, hopesGoalsAndAmbitionsDataSaga),
  ]);
  yield all([
    takeLatest(
      UPDATE_HOPES_GOALS_AMBITIONS_REQUEST,
      updateHopesGoalsAndAmbitionsSaga
    ),
  ]);
  yield all([
    takeLatest(ADD_PERSONAL_SMART_GOALS_REQUEST, addPersonalAndSmartGoalsSaga),
  ]);
  yield all([
    takeLatest(ADD_EDIT_GOALS_ASSESSMENT_REQUEST, addEditGoalsAssessmentSaga),
  ]);
  yield all([
    takeLatest(
      DELETE_PERSONAL_SMART_GOALS_REQUEST,
      deletePersonalAndSmartGoalsSaga
    ),
  ]);
  yield all([
    takeLatest(
      GET_PERSONAL_SMART_GOALS_REQUEST,
      getAllPersonalAndSmartGoalsSaga
    ),
  ]);
  yield all([
    takeLatest(
      GET_PERSONAL_SMART_GOALS_BY_SLP_ID_REQUEST,
      getPersonalAndSmartGoalsBySlpIdSaga
    ),
  ]);
  yield all([
    takeLatest(GET_CURRENT_SLP_GOALS_PDF_REQUEST, getCurrentSLPGoalsPDFSaga),
  ]);
  yield all([
    takeLatest(ADD_WHERE_TO_FROM_HERE_REQUEST, addWhereToFromHereSaga),
  ]);
}

export default slpFormSaga;
